import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CasButton, CasDisplayField, CasStackRow, CasSwitch, CasTabsView } from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import { formatDate, DateFormat, getRelativeTime } from 'src/compass/shared/util/casMoment';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';

import ClaimInfoTab from './ClaimInfoTab';
import { getColumnColorByDaysLeft, getColumnColorByStatus } from '../ClaimListing/ClaimListing';
import DeadReportsDialog from './DeadReports/DeadReportsDialog';
import ClaimTrackingStatusActions from './ClaimStatus/ClaimTrackingStatusActions';
import { ClaimCheckoutActionButton } from './ClaimStatus/ClaimCheckoutActionButton';
import ClaimCallbackDialog from './ClaimStatus/ClaimCallbackDialog';
import RequestDocDialog from './Requests/RequestDocDialog';
import GrowerDialog from './GrowerDialog';

import ActivityLogTab from '../../components/ActivityLogTab';

type Props = {
  data: any;
  onUpdated: (closeParentDialogOnSuccess?: boolean) => void;
};

function getClaimValue(data: any) {
  if(data?.hasClaim === "No Claim") return "No Claim";
  if( data?.hasClaim === "CLAIM" && data?.claimNumber === null) return "Converted";
  return data?.claimNumber;
}

export default function ClaimDetails({ data, onUpdated }: Props) {
  const deadReportsDialog = useCasBoolean();
  const callbackUpdateDialog = useCasBoolean();
  const requestDocDialog = useCasBoolean();
  const growerDialog = useCasBoolean();

  const [btnSubmitted, setBtnSubmitted] = useState(false);

  const tabList = [
    { label: 'Info', children: <ClaimInfoTab data={data} onUpdated={onUpdated} /> },
    { label: 'Activity Log', children: <ActivityLogTab endorsementId={data.id} /> },
  ];

  const { mutate, isPending } = useUpdateMutation({
    url: 'Sce/Claims/ToggleIsFwdContract',
    params: {
      endorsementId: data.id,
    },
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      onUpdated();
    },
    showLoader: false,
  });

  const { mutate: mutateDontSendInitial } = useUpdateMutation({
    url: 'Sce/Claims/ToggleDontSendInitial',
    params: {
      endorsementId: data.id,
    },
    onSuccess: (successData) => {
      onSuccessEnqueueSnackbar(successData);
      onUpdated();
    },
  });

  const onButtonUpdated = (closeParentDialogOnSuccess?: boolean) => {
    setBtnSubmitted(true);
    onUpdated(closeParentDialogOnSuccess);
  }

  return (
    <>
      {data?.deletedAtAIPFlag && (
        <Typography sx={{ textAlign: 'center', backgroundColor: 'yellow', padding: "4px" }} variant='h6'>Endorsement deleted at AIP</Typography>
      )}
      <CasStackRow className='bordered-cells'>
        <CasDisplayField label="Claim#" value={getClaimValue(data)}/>
        <CasDisplayField label="Type" value={data?.type} />
        <CasDisplayField label="HC" value={data?.totalNumberInsured} />
        <CasDisplayField
            label="Target (cwt)"
            value={data?.targetCWT}
          />          
        <CasDisplayField
            label="Coverage Price"
            value={data?.coveragePrice}
          />          
        <CasDisplayField
          label="Effective"
          value={formatDate(data?.effectiveDate, DateFormat.MDY)}
        />
        <CasDisplayField label="End" value={formatDate(data?.endDate, DateFormat.MDY)} />
        <CasDisplayField label="Relationship Owner" value={data?.servicingAgent} />
      </CasStackRow>

      <CasStackRow className='bordered-cells'>
        <CasDisplayField
          label="Status"
          value={<Box sx={getColumnColorByStatus(data)}>{data?.status}</Box>}
        />
        <CasDisplayField
          label="Days Left"
          value={<Box sx={getColumnColorByDaysLeft(data)}>{data?.daysLeft}</Box>}
        />
        <CasDisplayField
            label="Last Activity"
            value={<>
              {data?.lastActivity}
              &nbsp;
              {data?.lastActivity && 
              <Typography sx={{opacity:.5}} variant="caption">{getRelativeTime(data?.lastActivityDate)}</Typography>}
            </>}
          />          
        <CasDisplayField
          label="Is Fwd Contract?"
          value={
            <Box sx={{ backgroundColor: data?.isFwdContract ? 'yellow' : 'transparent' }}>
              {data?.isFwdContract ? "Yes" : "No"}
              {!!data?.isFwdContract && (
                <>
                  &nbsp;
                  {data?.forwardContractDate
                    ? formatDate(data.forwardContractDate, DateFormat.MDY)
                    : '(no date specified)'}
                </>
              )}
              &nbsp;
              {isPending ? (
                '(updating)'
              ) : (
                <CasLink onClick={() => mutate({ isFwdContract: data?.isFwdContract ? 0 : 1 })}>
                  toggle
                </CasLink>
              )}
              {!!data?.isFwdContract && (
                <Box>
                  {data?.initialFwdContractSend ? (
                    'Initial Sent'
                  ) : (
                    <CasSwitch
                      label="Don't Send initial"
                      checked={data.dontSendInitial ?? false}
                      onChange={(_, checked) => mutateDontSendInitial({ dontSendInitial: !data.dontSendInitial })}
                    />
                  )}
                </Box>
              )}
            </Box>
          }
        />
        <CasDisplayField
            label="What"
            value={data?.whatSent}
          />   
        <CasDisplayField
            label="Crop"
            value={data?.crop}
          />          
        <CasDisplayField
          label="State"
          value={data?.state}
        />
        <CasDisplayField
          label="County"
          value={data?.county}
        />
      </CasStackRow>

      {data?.callback?.assignedToEmployeeId && (
        <CasStackRow className="bordered-cells">
          <CasDisplayField
            label="Callback Assigned To"
            value={data?.callback?.assignedToEmployee}
            onClick={callbackUpdateDialog.setTrue}
          />
          <CasDisplayField
            label="Callback Notes"
            value={data?.callback?.callbackNotes}
            onClick={callbackUpdateDialog.setTrue}
          />
        </CasStackRow>
      )}

      <div style={{marginBottom: 6}}>
        <CasStackRow alignItems="center" justifyContent="space-between">
          <ClaimCheckoutActionButton data={data} onUpdated={onUpdated} />
          <CasStackRow>
            <CasButton variant="text" onClick={requestDocDialog.setTrue}>
              Request Doc
            </CasButton>
            {data?.growerId && (
              <CasButton
                variant="text"
                target="_blank"
                href={`${data?.eHarvestBaseUri}policy/Grower/Details?oid=${data.growerId}`}
                rel="noopener noreferrer"
              >
                eHarvest
              </CasButton>
            )}
            <CasButton
              variant="text"
              target="_blank"
              href={data.sharepointFolderURL}
              rel="noopener noreferrer"
              disabled={!data?.sharepointFolderURL}
            >
              Sharepoint
            </CasButton>
            {data?.growerId && (
              <CasButton variant="text" onClick={growerDialog.setTrue}>
                Grower
              </CasButton>
            )}
          </CasStackRow>
        </CasStackRow>
      </div>

      <CasStackRow gapSize="large" alignItems="center" backgroundColor="#ecffec">
        {btnSubmitted 
          ? <CasStackRow>
              <b>Action successful</b> 
              <CasButton
                variant="text"
                color="secondary"
                size="small"
                onClick={()=>setBtnSubmitted(false)}
              >
                Show next actions
              </CasButton>
            </CasStackRow>
          : <ClaimTrackingStatusActions data={data} onUpdated={onButtonUpdated} />
        }
        <CasDisplayField
          label="Deads Reported"
          value={`${data?.deadsReported}${data?.unspecified ? '+' : ''}`}
          horizontal
          onClick={deadReportsDialog.setTrue}
        />
      </CasStackRow>

      <CasTabsView tabList={tabList} />

      {deadReportsDialog.value && (
        <DeadReportsDialog
          data={data}
          onClose={() => {
            onUpdated();
            deadReportsDialog.setFalse();
          }}
        />
      )}

      {callbackUpdateDialog.value && (
        <ClaimCallbackDialog
          defaultValues={{
            id: data?.callback?.id,
            type: data?.callback?.type,
            callbackNotes: data?.callback?.callbackNotes,
            assignedToEmployeeId: data?.callback?.assignedToEmployeeId,
          }}
          title="Update Callback"
          submitButtonLabel="Update"
          mutationType="update"
          url="/Sce/Claims/CallbackUpdate"
          endorsementId={data?.id}
          onClose={callbackUpdateDialog.setFalse}
          onUpdated={onUpdated}
        />
      )}

      {requestDocDialog.value && (
        <RequestDocDialog
          ids={[data?.id]}
          onClose={requestDocDialog.setFalse}
          onUpdated={onUpdated}
        />
      )}

      {growerDialog.value && (
        <GrowerDialog
          id={data.growerId}
          insuredName={data.insuredName}
          onClose={growerDialog.setFalse}
          onUpdated={onUpdated}
        />
      )}
    </>
  );
}
